<!-- App.vue -->
<template>
  <div
    id="app"
    class="min-h-screen bg-gradient-to-br from-spicy-mustard-200 to-mocha-mousse-300 flex items-center justify-center relative text-mocha-mousse-900"
  >
    <GlobalLoadingOverlay />
    <GlobalErrorOverlay />
    <ModalOverlay />
    <!-- Development Badge -->
    <template v-if="development == true">
      <div class="absolute top-4 right-4 z-[9999]">
        <div
          class="bg-red-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse"
        >
          <span class="iconify w-5 h-5" data-icon="mdi:alert-circle"></span>
          <span class="font-semibold text-sm">Development Mode</span>
        </div>
      </div>
      <div
        class="absolute inset-0 border-4 border-red-500 pointer-events-none opacity-50 z-[9999]"
      ></div>
    </template>

    <!-- Fake VUO Badge -->
    <template v-if="fakeVUO == true">
      <div class="absolute top-16 right-4 z-50">
        <div
          class="bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
        >
          <span class="iconify w-5 h-5" data-icon="mdi:alert"></span>
          <span class="font-semibold text-sm">Fake VUO</span>
        </div>
      </div>
    </template>

    <!-- Auth Check Loading Overlay -->
    <template v-if="isCheckingAuth">
      <div
        class="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-[9998]"
      >
        <div class="flex flex-col items-center gap-4">
          <div
            class="w-12 h-12 border-4 border-spicy-mustard-400 border-t-transparent rounded-full animate-spin"
          ></div>
          <p class="text-mocha-mousse-700 font-medium">
            Verifying authentication...
          </p>
        </div>
      </div>
    </template>

    <router-view name="main" />
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import authService from "./services/authService";
import apiClient from "./services/api";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay.vue";
import GlobalErrorOverlay from "./components/GlobalErrorOverlay.vue";
import ModalOverlay from "./components/ModalOverlay.vue";

const isAuthenticated = ref(false);
const development = ref();
const fakeVUO = ref();
const isCheckingAuth = ref(true);

onMounted(async () => {
  // No proactive auth check - rely on server's 401/403 responses
  // The axios interceptor will handle session expiration
  // Storage event listener handles multi-tab sync
  isCheckingAuth.value = false;

  isAuthenticated.value = authService.getUserFromStorage();
  if (!isAuthenticated.value) {
    authService.getUserFromVuo();
    isAuthenticated.value = authService.getUserFromStorage();
  }

  console.log("isAuthenticated.value", isAuthenticated.value);

  try {
    const response = await apiClient.getDevelopment();
    console.log(
      "response.data.message.development",
      response.data.message.development,
    );
    development.value = response.data.message.development;
    console.log("development.value", development.value);
    localStorage.setItem("development", development.value);
  } catch (error) {
    if (error.response) {
      console.error(
        "Server Error:",
        error.response.status,
        error.response.data,
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    development.value = true;
    localStorage.setItem("development", development.value);
  }

  try {
    const fakeVUOResponse = await apiClient.getFakeVUO();
    console.log(
      "fakeVUOResponse.data.message.fake_vuo",
      fakeVUOResponse.data.message.fake_vuo,
    );
    fakeVUO.value = fakeVUOResponse.data.message.fake_vuo;
    console.log("fakeVUO.value", fakeVUO.value);
    localStorage.setItem("fakeVUO", fakeVUO.value);
  } catch (error) {
    if (error.response) {
      console.error(
        "Server Error:",
        error.response.status,
        error.response.data,
      );
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    fakeVUO.value = false;
    localStorage.setItem("fakeVUO", fakeVUO.value);
  }

  // Listen for storage changes from other tabs (e.g., logout in another tab)
  window.addEventListener("storage", (e) => {
    if (e.key === "user") {
      const newValue = e.newValue ? JSON.parse(e.newValue) : null;
      authService.saveUser(newValue);
    }
  });

  // Load Iconify if not already loaded
  if (!document.querySelector('script[src*="iconify"]')) {
    const script = document.createElement("script");
    script.src = "https://code.iconify.design/3/3.1.0/iconify.min.js";
    document.head.appendChild(script);
  }
});
</script>

<style>
#app {
  font-family: "Inter", "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
